// Marker detail drawer (docs/09 §4/§8, UI-LAW §4a Drawer).
// Shared by the agent-scoped and team-wide markers views: the ?marker=<id>
// subject renders here. The Revise/Automate route hand-offs render in BOTH
// scopes — their endpoints and the /markers/$markerId/edit route are
// id-addressed and need no agent_id (founder ruling 2026-07-20, issue #1015).
// The built-in firing control stays agent-scope-only (`builtinControl`
// present): the read/write configuration is per-agent and structural. Also
// owns the marker formatters both tables share, and the drawer-subject
// resolution hook (loaded pages first, GET /v1/markers/{id} fallback for
// deep links).

import { useQuery } from "@tanstack/react-query";

import type { components } from "../api/schema";
import { apiClient } from "../lib/apiClient";
import { ApiRequestError } from "../lib/errors";
import { formatDateTime } from "../lib/format";
import { queryKeys } from "../lib/queryKeys";
import { isShowcaseMode } from "../lib/showcaseMode";
import {
  Badge,
  BadgeGroup,
  Button,
  Copyable,
  Drawer,
  Grid,
  Inline,
  InlineError,
  KeyValue,
  Skeleton,
  Stack,
  Text,
} from "../ui";
import {
  MarkerRevisionHistory,
  type MarkerRevisionHistoryResult,
} from "./MarkerRevisionHistory";
import { ShowcaseReadOnlyNotice } from "./ShowcaseReadOnlyNotice";

type MarkerResponse = components["schemas"]["MarkerResponse"];
type MarkerRevisionResponse = components["schemas"]["MarkerRevisionResponse"];
type MarkerEvaluator = components["schemas"]["Evaluator"];
type MarkerOutput = components["schemas"]["MarkerOutput"];
type MarkerConfigurationItem = components["schemas"]["MarkerConfigurationItem"];
type MarkerConfigState = components["schemas"]["MarkerConfigState"];
type MarkerReviewPolicy = components["schemas"]["ReviewPolicy"];

// The navigation contract the Markers route container hands both views.
export interface MarkerListNavigation {
  onAutomate: (markerId: string) => void;
  onCloseMarker: () => void;
  onCreate: () => void;
  onOpenMarker: (markerId: string) => void;
  onRevise: (markerId: string) => void;
}

// Formatters shared by both marker tables and the drawer.
const EVALUATOR_LABELS: Record<MarkerEvaluator, string> = {
  builtin: "Built-in",
  judge: "Judge",
  rule: "Rule",
};

export function formatEvaluator(
  value: MarkerEvaluator | null | undefined,
): string {
  return value == null ? "Human-only" : EVALUATOR_LABELS[value];
}

const OUTPUT_LABELS: Record<MarkerOutput, string> = {
  boolean: "Boolean (true / false)",
  category: "Category",
  number: "Number",
};

export function formatOutput(value: MarkerOutput): string {
  return OUTPUT_LABELS[value];
}

const REVIEW_POLICY_LABELS: Record<MarkerReviewPolicy, string> = {
  always: "Always",
  never: "Never",
  sample: "Sample",
};

export function formatReview(revision: MarkerRevisionResponse): string {
  if (revision.review_policy !== "sample") {
    return REVIEW_POLICY_LABELS[revision.review_policy];
  }
  return `${REVIEW_POLICY_LABELS.sample} · ${String(revision.review_sample_rate ?? "")} %`;
}

export function formatLocator(revision: MarkerRevisionResponse): string {
  const { locator } = revision;
  switch (locator.kind) {
    case "whole_transcript":
      return "Whole transcript";
    case "audio_span":
      return `Audio · ${String(locator.start_ms)}–${String(locator.end_ms_exclusive)} ms`;
    case "utterance_range":
      return `Utterances · ${String(locator.start_index)}–${String(locator.end_index_exclusive)}`;
    case "char_span":
      return `Characters · ${String(locator.start_char)}–${String(locator.end_char_exclusive)}`;
    case "trace_node":
      return `Trace node · ${locator.node_id}`;
  }
}

export function MarkerLocatorChip({
  revision,
}: {
  revision: MarkerRevisionResponse;
}) {
  return (
    <Badge title="Transcript scope used to evaluate this marker">
      {formatLocator(revision)}
    </Badge>
  );
}

export function MarkerReviewChip({
  revision,
}: {
  revision: MarkerRevisionResponse;
}) {
  return (
    <Badge title="How often this marker is routed for human review">
      {formatReview(revision)}
    </Badge>
  );
}

// Drawer-subject resolution: the ?marker= id resolves from the loaded pages
// first; a deep link whose subject is not loaded falls back to the detail GET.
export interface MarkerDrawerSubject {
  errorMessage?: string;
  isOpen: boolean;
  isPending: boolean;
  subject?: MarkerResponse;
}

export function useMarkerDrawerSubject(
  markerId: string | undefined,
  loadedItems: MarkerResponse[],
): MarkerDrawerSubject {
  const loaded =
    markerId === undefined
      ? undefined
      : loadedItems.find((item) => item.id === markerId);
  const detail = useQuery({
    enabled: markerId !== undefined && loaded === undefined,
    queryKey: queryKeys.markers.detail(markerId ?? ""),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/markers/{id}", {
        params: { path: { id: markerId ?? "" } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  if (markerId === undefined) {
    return { isOpen: false, isPending: false };
  }
  if (loaded !== undefined) {
    return { isOpen: true, isPending: false, subject: loaded };
  }
  return {
    errorMessage: detail.isError ? detail.error.message : undefined,
    isOpen: true,
    isPending: detail.isPending,
    subject: detail.data,
  };
}

// Cells and controls shared with the tables.
export interface MarkerKindCellProps {
  marker: MarkerResponse;
}

export function MarkerKindCell({ marker }: MarkerKindCellProps) {
  const isBuiltIn = marker.builtin_key != null;
  const items = [
    {
      label: isBuiltIn ? "Built-in" : "Custom",
      title: "Where this marker comes from",
    },
    ...(marker.current_revision.locator.kind === "trace_node"
      ? [{ label: "Requires trace" }]
      : []),
  ];

  return <BadgeGroup items={items} />;
}

export interface BuiltinFiringControlProps {
  builtinKey: string | null;
  config?: MarkerConfigurationItem;
  onToggleBuiltin?: (builtinKey: string, nextState: MarkerConfigState) => void;
  pendingBuiltinKey?: string | null;
}

// Built-ins are system-managed catalog entries, but each agent may explicitly
// enable or disable either catalog class. A returned configuration item is the
// server-authoritative capability gate; an unreadable configuration stays
// read-only.
export function BuiltinFiringControl({
  builtinKey,
  config,
  onToggleBuiltin,
  pendingBuiltinKey = null,
}: BuiltinFiringControlProps) {
  const isTogglable =
    builtinKey != null && config != null && onToggleBuiltin != null;

  // The read-only state is carried next to the subject's name, so an
  // untogglable built-in renders no control here.
  if (!isTogglable) {
    return null;
  }

  const isOn = config.state === "on";
  const isPending = pendingBuiltinKey === builtinKey;
  // Serialize configure-markers writes: while ANY toggle is in flight every
  // toggle is disabled, so two concurrent POSTs can never race their echoed
  // full-config responses into the cache out of order.
  const isAnyTogglePending = pendingBuiltinKey != null;

  return (
    <Inline align="center" gap={2}>
      <Badge tone={isOn ? "success" : "neutral"}>{isOn ? "On" : "Off"}</Badge>
      <Button
        disabled={isAnyTogglePending}
        size="sm"
        variant="secondary"
        onClick={() => {
          onToggleBuiltin(builtinKey, isOn ? "off" : "on");
        }}
      >
        {isPending ? "Saving..." : isOn ? "Disable" : "Enable"}
      </Button>
    </Inline>
  );
}

// Passed only by the agent-scoped view: its presence is the "agent scope"
// signal that renders the built-in firing control for built-in subjects. The
// Revise/Automate authoring actions are id-addressed and render in both scopes.
export interface MarkerDrawerBuiltinControl {
  config?: MarkerConfigurationItem;
  onToggleBuiltin?: (builtinKey: string, nextState: MarkerConfigState) => void;
  pendingBuiltinKey?: string | null;
}

export interface MarkerDetailDrawerProps {
  // Team-wide view only: adds the Agent row to the metadata grid.
  agentNameById?: Record<string, string>;
  builtinControl?: MarkerDrawerBuiltinControl;
  drawer: MarkerDrawerSubject;
  history: MarkerRevisionHistoryResult;
  onAutomate: (markerId: string) => void;
  onDismiss: () => void;
  onRevise: (markerId: string) => void;
}

function agentValue(
  marker: MarkerResponse,
  agentNameById: Record<string, string>,
) {
  if (marker.agent_id == null) {
    return <Badge>Built-in</Badge>;
  }
  const name = agentNameById[marker.agent_id];
  if (name !== undefined) {
    return <Text as="span">{name}</Text>;
  }
  return (
    <Text as="span" variant="muted">
      —
    </Text>
  );
}

export function MarkerDetailDrawer({
  agentNameById,
  builtinControl,
  drawer,
  history,
  onAutomate,
  onDismiss,
  onRevise,
}: MarkerDetailDrawerProps) {
  const subject = drawer.subject;

  return (
    <Drawer
      ariaLabel="Marker detail"
      isOpen={drawer.isOpen}
      onDismiss={onDismiss}
      size="lg"
    >
      {drawer.isPending ? (
        <Skeleton label="Loading marker" rows={6} shape="detail-field" />
      ) : null}
      {drawer.errorMessage != null ? (
        <InlineError>{drawer.errorMessage}</InlineError>
      ) : null}
      {subject !== undefined ? (
        <MarkerDetailBody
          agentNameById={agentNameById}
          builtinControl={builtinControl}
          history={history}
          marker={subject}
          onAutomate={onAutomate}
          onRevise={onRevise}
        />
      ) : null}
    </Drawer>
  );
}

interface MarkerDetailBodyProps {
  agentNameById?: Record<string, string>;
  builtinControl?: MarkerDrawerBuiltinControl;
  history: MarkerRevisionHistoryResult;
  marker: MarkerResponse;
  onAutomate: (markerId: string) => void;
  onRevise: (markerId: string) => void;
}

function MarkerDetailBody({
  agentNameById,
  builtinControl,
  history,
  marker,
  onAutomate,
  onRevise,
}: MarkerDetailBodyProps) {
  const revision = marker.current_revision;
  const isBuiltIn = marker.builtin_key != null;
  const canAutomate = !isBuiltIn && revision.evaluator == null;
  const rows = [
    {
      label: isBuiltIn ? "Catalog" : "Version",
      value: isBuiltIn
        ? `System catalog · version ${String(revision.revision)}`
        : String(revision.revision),
    },
    { label: "Output", value: formatOutput(revision.output) },
    { label: "Evaluator", value: formatEvaluator(revision.evaluator) },
    { label: "Locator", value: <MarkerLocatorChip revision={revision} /> },
    { label: "Review policy", value: <MarkerReviewChip revision={revision} /> },
    { label: "Updated", value: formatDateTime(marker.updated_at) },
    ...(agentNameById !== undefined
      ? [{ label: "Agent", value: agentValue(marker, agentNameById) }]
      : []),
    {
      label: "id",
      value: <Copyable label="Marker ID" value={marker.id} />,
    },
  ];
  const midpoint = Math.ceil(rows.length / 2);

  return (
    <Stack gap={4}>
      <Inline align="baseline" gap={2}>
        <Text>
          <strong>{revision.display_name}</strong>
        </Text>
        {isBuiltIn ? <Text variant="muted">Read-only</Text> : null}
      </Inline>
      <Text>{revision.description ?? revision.display_name}</Text>
      <MarkerKindCell marker={marker} />
      {builtinControl?.config?.exclusion_reason ? (
        <Text variant="muted">
          {builtinControl.config.exclusion_reason.message}
        </Text>
      ) : null}
      <Grid columns={2} gap={4}>
        <KeyValue rows={rows.slice(0, midpoint)} />
        <KeyValue rows={rows.slice(midpoint)} />
      </Grid>
      {!isBuiltIn ? (
        <MarkerRevisionHistory
          currentRevisionId={revision.id}
          history={history}
        />
      ) : null}
      {isBuiltIn && builtinControl !== undefined ? (
        <BuiltinFiringControl
          builtinKey={marker.builtin_key}
          config={builtinControl.config}
          onToggleBuiltin={builtinControl.onToggleBuiltin}
          pendingBuiltinKey={builtinControl.pendingBuiltinKey}
        />
      ) : null}
      {/* Authoring actions render in both scopes (issue #1015): the revise /
          automate endpoints and the /markers/$markerId/edit route are
          id-addressed, so the team-wide drawer offers them too. The built-in
          firing control above stays agent-scope-only — its config is
          per-agent and structural. */}
      {isShowcaseMode ? (
        <ShowcaseReadOnlyNotice />
      ) : !isBuiltIn ? (
        <Inline gap={2}>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              onRevise(marker.id);
            }}
          >
            Revise
          </Button>
          {canAutomate ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                onAutomate(marker.id);
              }}
            >
              Automate
            </Button>
          ) : null}
        </Inline>
      ) : null}
    </Stack>
  );
}
